import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../components/admin/AdminPage';
import MetricCard from '../components/admin/MetricCard';
import SectionCard from '../components/admin/SectionCard';
import { LoadingState } from '../components/admin/StateViews';
import {
  AdminAuditEntry,
  AdminSettings,
  AdminUser,
  Cause,
  ImpactStory,
} from '../types';
import { CauseService } from '../services/CauseService';
import { ImpactStoryService } from '../services/ImpactStoryService';
import { AuditLogService } from '../services/AuditLogService';
import { AdminSettingsService } from '../services/AdminSettingsService';
import { AdminUserService } from '../services/AdminUserService';
import StatusChip from '../components/admin/StatusChip';
import { useAuth } from '../hooks/useAuth';

const formatRelative = (value: string) => {
  const elapsed = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.round(elapsed / (1000 * 60)));

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [causes, setCauses] = useState<Cause[]>([]);
  const [stories, setStories] = useState<ImpactStory[]>([]);
  const [auditEntries, setAuditEntries] = useState<AdminAuditEntry[]>([]);
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      const [causeResult, storyResult, auditResult, settingsResult, usersResult] =
        await Promise.allSettled([
          CauseService.getCauses(),
          ImpactStoryService.getImpactStories(),
          AuditLogService.listEntries(),
          AdminSettingsService.getSettings(),
          AdminUserService.getUsers(),
        ]);

      if (causeResult.status === 'fulfilled') {
        setCauses(causeResult.value);
      }

      if (storyResult.status === 'fulfilled') {
        setStories(storyResult.value);
      }

      if (auditResult.status === 'fulfilled') {
        setAuditEntries(auditResult.value);
      }

      if (settingsResult.status === 'fulfilled') {
        setSettings(settingsResult.value);
      }

      if (usersResult.status === 'fulfilled') {
        setUsers(usersResult.value);
      }

      if (
        causeResult.status === 'rejected' ||
        storyResult.status === 'rejected'
      ) {
        setError(
          'Some live dashboard data could not be loaded. Admin placeholders remain available.'
        );
      }

      setLoading(false);
    };

    loadDashboard();
  }, []);

  const metrics = useMemo(() => {
    const publishedCauses = causes.filter((cause) => cause.status === 'published');
    const featuredCauses = causes.filter((cause) => cause.featured);
    const rankedCauses = causes.filter((cause) => cause.rank !== null);
    const sourcedCauses = causes.filter((cause) => Boolean(cause.externalProgramId));
    const activeUsers = users.filter((adminUser) => adminUser.status === 'active');

    return {
      totalCauses: causes.length,
      publishedCauses: publishedCauses.length,
      featuredCauses: featuredCauses.length,
      rankedCauses: rankedCauses.length,
      sourcedCauses: sourcedCauses.length,
      totalStories: stories.length,
      activeUsers: activeUsers.length,
      totalUsers: users.length,
    };
  }, [causes, stories, users]);

  if (loading) {
    return <LoadingState label="Loading dashboard overview…" />;
  }

  return (
    <AdminPage
      title="Overview"
      description={`Welcome back, ${user?.email || 'admin'}. This dashboard surfaces the current platform state, recent admin activity, and the quickest next actions.`}
      action={
        <Button variant="contained" onClick={() => navigate('/causes/new')}>
          Create Cause
        </Button>
      }
    >
      {error ? <Alert severity="warning">{error}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            label="Causes live"
            value={String(metrics.totalCauses)}
            helperText={`${metrics.publishedCauses} published • ${metrics.sourcedCauses} with source IDs`}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            label="Featured causes"
            value={String(metrics.featuredCauses)}
            helperText={`${metrics.rankedCauses} with rank assigned`}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            label="Impact stories"
            value={String(metrics.totalStories)}
            helperText="Content available to surface"
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            label="Active users"
            value={String(metrics.activeUsers)}
            helperText={`${metrics.totalUsers} total synced users`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <SectionCard
            title="Cause health"
            description="A quick read on publishing coverage and feature readiness."
          >
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Published coverage</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {metrics.totalCauses > 0
                      ? `${Math.round(
                          (metrics.publishedCauses / metrics.totalCauses) * 100
                        )}%`
                      : '0%'}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={
                    metrics.totalCauses > 0
                      ? (metrics.publishedCauses / metrics.totalCauses) * 100
                      : 0
                  }
                  sx={{ height: 10, borderRadius: 999 }}
                />
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Feature flag readiness</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settings?.featureFlags.featuredCauses ? 'Enabled' : 'Needs attention'}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={settings?.featureFlags.featuredCauses ? 100 : 35}
                  color={settings?.featureFlags.featuredCauses ? 'primary' : 'warning'}
                  sx={{ height: 10, borderRadius: 999 }}
                />
              </Stack>
              <Grid container spacing={2}>
                {causes.slice(0, 4).map((cause) => (
                  <Grid item xs={12} sm={6} key={cause.id}>
                    <Stack
                      spacing={1}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography fontWeight={600}>{cause.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {cause.externalProgramId || cause.id} • Rank {cause.rank ?? 'N/A'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StatusChip value={cause.status} />
                        {cause.featured ? <StatusChip value="featured" /> : null}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {cause.shortDescription || cause.description.slice(0, 96)}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Quick actions"
            description="Common tasks for daily admin workflows."
          >
            <Stack spacing={1.5}>
              <Button variant="contained" onClick={() => navigate('/causes/new')}>
                Add a new cause
              </Button>
              <Button variant="outlined" onClick={() => navigate('/content/stories')}>
                Manage impact stories
              </Button>
              <Button variant="outlined" onClick={() => navigate('/analytics')}>
                Export analytics
              </Button>
              <Button variant="outlined" onClick={() => navigate('/settings')}>
                Review settings
              </Button>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Recent activity"
            description="Audit-friendly updates captured from admin actions."
          >
            <List disablePadding>
              {auditEntries.slice(0, 6).map((entry) => (
                <ListItem key={entry.id} disableGutters divider>
                  <ListItemText
                    primary={entry.description}
                    secondary={`${entry.actorEmail} • ${formatRelative(entry.createdAt)}`}
                  />
                </ListItem>
              ))}
            </List>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Platform controls"
            description="A readout of configuration areas that can be managed without code changes."
          >
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>Featured causes</Typography>
                <StatusChip
                  value={settings?.featureFlags.featuredCauses ? 'active' : 'paused'}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>Impact stories</Typography>
                <StatusChip
                  value={settings?.featureFlags.impactStories ? 'active' : 'paused'}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>Analytics export</Typography>
                <StatusChip
                  value={settings?.featureFlags.analyticsExports ? 'active' : 'paused'}
                />
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </AdminPage>
  );
};

export default DashboardPage;
